import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  Chip,
  InputAdornment,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { IPedido } from "../types/index";
import {
  Add,
  AttachMoney,
  Delete,
  AccountBalanceWallet,
} from "@mui/icons-material";
import {
  getLancamentosByDocumento,
  adicionarPagamentoLancamento,
  salvarPedido,
  excluirPedido,
  excluirLancamento,
  createLancamentoFromPedido,
} from "../services/index";

interface Props {
  open: boolean;
  onClose: () => void;
  pedido: IPedido | null;
  onSave?: () => void;
}

interface Pagamento {
  id: string;
  valor: number;
  formaPagamento: string;
  dataPagamento: string;
  observacoes?: string;
}

interface LancamentoApi {
  Id: string;
  Codigo: number;
  NumeroDocumento: string;
  Valor: number;
  TotalRecebido: number;
  Quitado: boolean;
  Pagamentos?: Array<{
    Data: string;
    FormaPagamento: string;
    Valor: number;
    NumeroDocumento?: string;
    Conciliado: boolean;
  }>;
}

const ModalEditarPedido: React.FC<Props> = ({
  open,
  onClose,
  pedido,
  onSave,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [observacoes, setObservacoes] = useState("");
  const [pagamentos, setPagamentos] = useState<Pagamento[]>([]);
  const [lancamentoAtual, setLancamentoAtual] = useState<LancamentoApi | null>(
    null
  );
  const [carregandoLancamento, setCarregandoLancamento] = useState(false);
  const [novoPagamento, setNovoPagamento] = useState({
    valor: 0,
    formaPagamento: "",
    dataPagamento: new Date().toISOString().split("T")[0],
    observacoes: "",
  });
  const [valorFormatado, setValorFormatado] = useState("0,00");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [criandoLancamento, setCriandoLancamento] = useState(false);

  useEffect(() => {
    const carregarLancamento = async () => {
      if (pedido) {
        setObservacoes(pedido.Descricao || "");
        setCarregandoLancamento(true);
        setErro(null);

        try {
          // Buscar lançamento pelo número do documento (ex: "Pedido n°102")
          const numeroDocumento = `Pedido n°${pedido.Codigo}`;
          const lancamentos = await getLancamentosByDocumento(numeroDocumento);

          if (lancamentos && lancamentos.length > 0) {
            const lancamento = lancamentos[0];
            // Lançamento encontrado com sucesso
            setLancamentoAtual(lancamento);

            // Carregar pagamentos existentes
            const pagamentosExistentes =
              lancamento.Pagamentos?.map((pag: any, index: number) => {
                // Processando pagamento
                return {
                  id: `existing-${index}`,
                  valor: pag.Valor || 0,
                  formaPagamento: pag.FormaPagamento || "",
                  dataPagamento: pag.Data ? pag.Data.split(" - ")[0] : "",
                  observacoes: pag.NumeroDocumento || "",
                };
              }) || [];

            // Pagamentos processados com sucesso
            setPagamentos(pagamentosExistentes);
          } else {
            // Se não encontrar lançamento, limpar os dados
            setLancamentoAtual(null);
            setPagamentos([]);
          }
        } catch (error) {
          setErro("Erro ao carregar dados do lançamento");
          setLancamentoAtual(null);
          setPagamentos([]);
        } finally {
          setCarregandoLancamento(false);
        }
      }
    };

    if (open && pedido) {
      carregarLancamento();
    } else {
      // Reset quando fechar o modal
      setLancamentoAtual(null);
      setPagamentos([]);
      setObservacoes("");
      setErro(null);
      setNovoPagamento({
        valor: 0,
        formaPagamento: "",
        dataPagamento: new Date().toISOString().split("T")[0],
        observacoes: "",
      });
      setValorFormatado("0,00");
    }
  }, [pedido, open]);

  const handleAdicionarPagamento = async () => {
    // Usar o valor formatado atual como fonte de verdade
    const valorAtual = converterParaNumero(valorFormatado);

    if (!valorAtual || valorAtual <= 0 || !novoPagamento.formaPagamento) {
      setErro("Valor e forma de pagamento são obrigatórios");
      return;
    }

    if (!lancamentoAtual) {
      setErro("Lançamento não encontrado. Não é possível adicionar pagamento.");
      return;
    }

    if (saldoRestante <= 0) {
      setErro(
        "Este pedido já está totalmente pago. Não é possível adicionar mais pagamentos."
      );
      return;
    }

    // Arredondar para centavos para evitar problemas de precisão de ponto flutuante
    const valorAtualCentavos = Math.round(valorAtual * 100);
    const saldoRestanteCentavos = Math.round(saldoRestante * 100);

    if (valorAtualCentavos > saldoRestanteCentavos) {
      setErro(
        `O valor do pagamento (R$ ${valorAtual.toFixed(2)}) não pode ser maior que o saldo restante (R$ ${saldoRestante.toFixed(2)}).`
      );
      return;
    }

    try {
      setSalvando(true);
      setErro(null);

      // Preparar o pagamento para a API usando o valor atual
      const pagamentoParaApi = {
        data: new Date(novoPagamento.dataPagamento).toISOString(),
        formaPagamento: novoPagamento.formaPagamento,
        numeroDocumento:
          novoPagamento.observacoes || `Pedido n°${pedido?.Codigo}`,
        contaBancaria: "",
        conciliado: true,
        valor: valorAtual,
      };

      // Adicionar pagamento via API
      await adicionarPagamentoLancamento(lancamentoAtual.Codigo, [
        pagamentoParaApi,
      ]);

      // Adicionar localmente para atualizar a interface usando o valor correto
      const pagamento: Pagamento = {
        id: `new-${Date.now()}`,
        ...novoPagamento,
        valor: valorAtual,
      };

      setPagamentos([...pagamentos, pagamento]);
      setNovoPagamento({
        valor: 0,
        formaPagamento: "",
        dataPagamento: new Date().toISOString().split("T")[0],
        observacoes: "",
      });
      setValorFormatado("0,00");

      // Pagamento adicionado com sucesso
    } catch (error) {
      setErro("Erro ao adicionar pagamento. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  };

  // Funções para formatação de moeda tipo calculadora
  const formatarMoeda = (valor: string): string => {
    // Remove tudo que não é dígito
    const digitos = valor.replace(/\D/g, "");

    if (digitos === "" || digitos === "0") {
      return "0,00";
    }

    // Converte para centavos e formata
    const centavos = parseInt(digitos);
    const reais = centavos / 100;

    return reais.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const converterParaNumero = (valorFormatado: string): number => {
    const valor = valorFormatado.replace(",", ".");
    const numero = parseFloat(valor) || 0;
    // Arredondar para 2 casas decimais para evitar problemas de precisão
    return Math.round(numero * 100) / 100;
  };

  // Função para sincronizar o valor formatado com o estado do novo pagamento
  const sincronizarValor = useCallback((novoValorFormatado: string) => {
    const novoValorNumerico = converterParaNumero(novoValorFormatado);
    setValorFormatado(novoValorFormatado);
    setNovoPagamento((prev) => ({ ...prev, valor: novoValorNumerico }));
  }, []);

  const handleValorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target.value;

      // Se for backspace ou delete, remove o último dígito
      if (input === "" || input === "0") {
        sincronizarValor("0,00");
        return;
      }

      const novoValorFormatado = formatarMoeda(input);
      sincronizarValor(novoValorFormatado);
    },
    [sincronizarValor]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Permitir apenas números, backspace, delete e algumas teclas de navegação
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ];

      if (allowedKeys.includes(event.key)) {
        if (event.key === "Backspace" || event.key === "Delete") {
          // Remove o último dígito
          const valorAtual = valorFormatado.replace(/\D/g, "");
          const novoValor = valorAtual.slice(0, -1);
          const novoValorFormatado = formatarMoeda(novoValor);
          sincronizarValor(novoValorFormatado);
          event.preventDefault();
        }
        return;
      }

      // Permitir apenas dígitos
      if (!/\d/.test(event.key)) {
        event.preventDefault();
        return;
      }

      // Adicionar o dígito
      const valorAtual = valorFormatado.replace(/\D/g, "");
      const novoValor = valorAtual + event.key;
      const novoValorFormatado = formatarMoeda(novoValor);
      sincronizarValor(novoValorFormatado);
      event.preventDefault();
    },
    [valorFormatado, sincronizarValor]
  );

  // Memoizar o cálculo do total de pagamentos
  const totalPagamentos = useMemo(() => {
    return pagamentos.reduce((total, pag) => total + pag.valor, 0);
  }, [pagamentos]);

  // Memoizar o cálculo do saldo restante
  const saldoRestante = useMemo(() => {
    const valorTotal = lancamentoAtual?.Valor || pedido?.ValorFinal || 0;
    return valorTotal - totalPagamentos;
  }, [lancamentoAtual?.Valor, pedido?.ValorFinal, totalPagamentos]);

  // Valores memoizados otimizados para performance

  const handleSalvar = async () => {
    setSalvando(true);
    setErro(null);

    try {
      // Salvar observações do pedido na API externa usando /Pedidos/Salvar
      if (
        pedido &&
        pedido.Codigo &&
        observacoes !== (pedido?.Descricao || "")
      ) {
        await salvarPedido(pedido, observacoes);
      }

      // Alterações salvas com sucesso
      onSave?.();
      onClose();
    } catch (error) {
      setErro("Erro ao salvar alterações. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  };

  const handleAbrirConfirmacaoExclusao = () => {
    if (!pedido) return;
    setConfirmDeleteModal(true);
  };

  const handleExcluir = async () => {
    if (!pedido || !pedido.Codigo) return;

    setExcluindo(true);
    setErro(null);

    try {
      // Primeiro, excluir o lançamento (se existir)
      if (lancamentoAtual && lancamentoAtual.Codigo) {
        await excluirLancamento(lancamentoAtual.Codigo);
      }

      // Depois, excluir o pedido
      await excluirPedido(pedido.Codigo);

      // Fechar modais e atualizar a lista
      setConfirmDeleteModal(false);
      onClose();
      onSave?.();
    } catch (error: any) {
      // Verificar se é erro de pagamento conciliado
      if (error.response?.status === 400 && error.response?.data?.message) {
        setErro(error.response.data.message);
      } else {
        setErro("Erro ao excluir. Tente novamente.");
      }
    } finally {
      setExcluindo(false);
    }
  };

  if (!pedido) return null;

  const formasPagamento = [
    "Dinheiro",
    "Cartão de Crédito",
    "Cartão de Débito",
    "PIX",
    // "Transferência",
    "Boleto Bancário",
    // "Cheque",
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AttachMoney color="primary" />
          <Box>
            <Typography variant="h6">Editar Pedido #{pedido.Codigo}</Typography>
            <Typography variant="caption" color="text.secondary">
              Cliente: {pedido.Cliente}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {erro && (
            <Alert severity="error" onClose={() => setErro(null)}>
              {erro}
            </Alert>
          )}

          {!carregandoLancamento && !lancamentoAtual && !erro && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  startIcon={<AccountBalanceWallet />}
                  onClick={async () => {
                    if (!pedido) return;
                    setCriandoLancamento(true);
                    setErro(null);
                    try {
                      const result = await createLancamentoFromPedido(pedido);
                      if (result.success) {
                        // Recarregar o lançamento criado
                        const numeroDocumento = `Pedido n°${pedido.Codigo}`;
                        const lancamentos =
                          await getLancamentosByDocumento(numeroDocumento);
                        if (lancamentos && lancamentos.length > 0) {
                          setLancamentoAtual(lancamentos[0]);
                        }
                      }
                    } catch (error: any) {
                      setErro(error.message || "Erro ao criar lançamento");
                    } finally {
                      setCriandoLancamento(false);
                    }
                  }}
                  disabled={criandoLancamento}
                >
                  {criandoLancamento ? "Criando..." : "Criar Lançamento"}
                </Button>
              }
            >
              Nenhum lançamento financeiro encontrado para este pedido. Clique
              no botão ao lado para criar um lançamento vinculado.
            </Alert>
          )}

          {lancamentoAtual &&
            (saldoRestante <= 0 || lancamentoAtual.Quitado) && (
              <Alert severity="success">
                Pedido quitado!
                {saldoRestante <= 0
                  ? " Este lançamento está marcado como quitado"
                  : " Este lançamento está marcado como quitado"}{" "}
                - não é possível adicionar mais pagamentos.
              </Alert>
            )}

          {/* Resumo do Pedido */}
          <Paper sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Resumo do Pedido
            </Typography>
            {carregandoLancamento ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Carregando dados financeiros...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Valor Total
                  </Typography>
                  <Typography variant="h6" color="primary">
                    R${" "}
                    {(lancamentoAtual?.Valor || pedido.ValorFinal || 0).toFixed(
                      2
                    )}
                  </Typography>
                  {lancamentoAtual && (
                    <Typography variant="caption" color="text.secondary">
                      Lançamento #{lancamentoAtual.Codigo}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Pago
                  </Typography>
                  <Typography variant="h6" color="info.main">
                    R$ {totalPagamentos.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Saldo Restante
                  </Typography>
                  <Typography
                    variant="h6"
                    color={saldoRestante <= 0 ? "success.main" : "warning.main"}
                  >
                    R$ {saldoRestante.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Observações */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Observações do Pedido
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Adicione observações sobre o pedido..."
              variant="outlined"
            />
          </Box>

          {/* Pagamentos Existentes */}
          {pagamentos.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Pagamentos Registrados
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {pagamentos.map((pagamento) => (
                  <Paper
                    key={pagamento.id}
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        R$ {pagamento.valor.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pagamento.formaPagamento} • {pagamento.dataPagamento}
                      </Typography>
                      {pagamento.observacoes && (
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          {pagamento.observacoes}
                        </Typography>
                      )}
                    </Box>
                    {/* <IconButton
                      onClick={() => handleRemoverPagamento(pagamento.id)}
                      color="error"
                      size="small"
                    >
                      <Delete />
                    </IconButton> */}
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {/* Adicionar Novo Pagamento */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Adicionar Pagamento
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                  <TextField
                    fullWidth
                    label="Valor"
                    type="text"
                    value={valorFormatado}
                    onChange={handleValorChange}
                    onKeyDown={handleKeyDown}
                    disabled={saldoRestante <= 0}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">R$</InputAdornment>
                      ),
                    }}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9,]*",
                    }}
                    helperText="Digite apenas números. Ex: 1234 = R$ 12,34"
                  />
                </Box>
                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                  <FormControl fullWidth disabled={saldoRestante <= 0}>
                    <InputLabel>Forma de Pagamento</InputLabel>
                    <Select
                      value={novoPagamento.formaPagamento}
                      onChange={(e) =>
                        setNovoPagamento({
                          ...novoPagamento,
                          formaPagamento: e.target.value,
                        })
                      }
                      label="Forma de Pagamento"
                    >
                      {formasPagamento.map((forma) => (
                        <MenuItem key={forma} value={forma}>
                          {forma}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                  <TextField
                    fullWidth
                    label="Data do Pagamento"
                    type="date"
                    value={novoPagamento.dataPagamento}
                    onChange={(e) =>
                      setNovoPagamento({
                        ...novoPagamento,
                        dataPagamento: e.target.value,
                      })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Box sx={{ flex: "1 1 200px", minWidth: "200px" }}>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAdicionarPagamento}
                    fullWidth
                    sx={{ height: 56 }}
                    disabled={
                      salvando ||
                      carregandoLancamento ||
                      !lancamentoAtual ||
                      saldoRestante <= 0 ||
                      lancamentoAtual?.Quitado === true
                    }
                  >
                    {saldoRestante <= 0 || lancamentoAtual?.Quitado === true
                      ? "Totalmente Pago"
                      : salvando
                        ? "Adicionando..."
                        : "Adicionar Pagamento"}
                  </Button>
                </Box>
              </Box>
              <TextField
                fullWidth
                label="Observações do Pagamento (opcional)"
                value={novoPagamento.observacoes}
                onChange={(e) =>
                  setNovoPagamento({
                    ...novoPagamento,
                    observacoes: e.target.value,
                  })
                }
                size="small"
                disabled={
                  saldoRestante <= 0 || lancamentoAtual?.Quitado === true
                }
              />
            </Box>
          </Box>

          {/* Status de Pagamento */}
          {totalPagamentos > 0 && (
            <Paper sx={{ p: 2, backgroundColor: "#e8f5e8" }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Status do Pagamento
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  label={`Total Pago: R$ ${totalPagamentos.toFixed(2)}`}
                  color="success"
                  variant="outlined"
                />
                {saldoRestante > 0 && !lancamentoAtual?.Quitado ? (
                  <Chip
                    label={`Saldo: R$ ${saldoRestante.toFixed(2)}`}
                    color="warning"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    label={
                      lancamentoAtual?.Quitado
                        ? "Quitado na API"
                        : "Pago Integralmente"
                    }
                    color="success"
                  />
                )}
              </Box>
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: isMobile ? 1 : 2,
          px: isMobile ? 1 : 4.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            gap: 1,
          }}
        >
          {!isMobile && (
            <Button
              startIcon={<Delete />}
              onClick={handleAbrirConfirmacaoExclusao}
              disabled={salvando || excluindo}
              color="error"
              variant="outlined"
            >
              Excluir
            </Button>
          )}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              width: isMobile ? "100%" : "auto",
              ml: isMobile ? 0 : "auto",
            }}
          >
            {isMobile && (
              <IconButton
                onClick={handleAbrirConfirmacaoExclusao}
                disabled={salvando || excluindo}
                color="error"
                size="small"
                sx={{
                  border: "1px solid",
                  borderColor: "error.main",
                }}
              >
                <Delete />
              </IconButton>
            )}
            <Button
              onClick={onClose}
              disabled={salvando || excluindo}
              size={isMobile ? "small" : "medium"}
              sx={{ flex: isMobile ? 1 : "none" }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSalvar}
              disabled={salvando || excluindo}
              size={isMobile ? "small" : "medium"}
              sx={{
                minWidth: isMobile ? "auto" : 120,
                flex: isMobile ? 1 : "none",
              }}
            >
              {salvando
                ? "Salvando..."
                : isMobile
                  ? "Salvar"
                  : "Salvar Alterações"}
            </Button>
          </Box>
        </Box>
      </DialogActions>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={confirmDeleteModal}
        onClose={() => setConfirmDeleteModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Tem certeza que deseja excluir este pedido/atendimento?
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta ação não pode ser desfeita. O pedido e o lançamento financeiro
            associado serão excluídos permanentemente do sistema ERP.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              Detalhes do que será excluído:
            </Typography>
            <Paper sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
              <Typography variant="body2">
                <strong>Pedido:</strong> #{pedido?.Codigo} -{" "}
                {pedido?.Descricao || "Sem descrição"}
              </Typography>
              <Typography variant="body2">
                <strong>Cliente:</strong> {pedido?.Cliente}
              </Typography>
              <Typography variant="body2">
                <strong>Valor:</strong> R${" "}
                {pedido?.ValorFinal?.toFixed(2) || "0.00"}
              </Typography>
              {lancamentoAtual && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    <strong>Lançamento:</strong> #{lancamentoAtual.Codigo} -{" "}
                    {lancamentoAtual.NumeroDocumento}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Valor do Lançamento:</strong> R${" "}
                    {lancamentoAtual.Valor?.toFixed(2) || "0.00"}
                  </Typography>
                </>
              )}
            </Paper>
          </Box>
          {erro && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {erro}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3 }}>
          <Button
            onClick={() => {
              setConfirmDeleteModal(false);
              setErro(null);
            }}
            disabled={excluindo}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleExcluir}
            disabled={excluindo}
            sx={{ minWidth: 120 }}
          >
            {excluindo ? "Excluindo..." : "Confirmar Exclusão"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ModalEditarPedido;
